import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { scheduleFollowUpReminder } from "../utils/reminder";
import NotificationsIcon from "@mui/icons-material/Notifications";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useOrganization } from "../utils/OrganizationContext";
import ErrorModal from "../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";

const PAGE_SIZE = 20;

// Stat cards (multi-hue, like the design)
const STAT_THEMES = {
  UPCOMING: { bg: "#FDF4E1", edge: "#F3E0B4", label: "#8A5A08", num: "#8A5A08" },
  TODAY: { bg: "#EAF2FB", edge: "#C9DEF3", label: "#1B4F82", num: "#1B4F82" },
  TEAM: { bg: "#EFEDFB", edge: "#D3D0F2", label: "#4A3FBF", num: "#4A3FBF" },
  PAST: { bg: "#E9F7EE", edge: "#C4E8D2", label: "#1B6B41", num: "#1B6B41" },
};

// Status pills
const PILL = {
  Pending: { bg: "#FDF1D6", edge: "#F3DDA6", color: "#8A5A08" },
  Today: { bg: "#EAF2FB", edge: "#C9DEF3", color: "#1B4F82" },
  Past: { bg: "#E9F7EE", edge: "#C4E8D2", color: "#1B6B41" },
};

const metaLabel = { fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", color: MUTED };
const smallBtn = {
  height: 30,
  minWidth: 0,
  px: 1.5,
  borderRadius: "8px",
  border: `1px solid ${FIELD_EDGE}`,
  background: "#FFFFFF",
  color: INK,
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { background: "#EAF2FB", borderColor: "#C9DEF3", color: "#1B4F82" },
};
const dangerBtn = {
  ...smallBtn,
  color: "#A6342A",
  borderColor: "#F2CFCA",
  "&:hover": { background: "#FBEDEB", borderColor: "#F2CFCA", color: "#A6342A" },
};
const pillBtn = (on) => ({
  height: 32,
  minWidth: 0,
  px: 1.5,
  gap: 0.5,
  borderRadius: "999px",
  border: `1px solid ${on ? "#C9DEF3" : FIELD_EDGE}`,
  background: on ? "#EAF2FB" : "#FFFFFF",
  color: on ? "#1B4F82" : INK,
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: 600,
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": { background: "#F4F7FA" },
});
const menuPaper = {
  sx: {
    mt: 0.5,
    borderRadius: "12px",
    border: `1px solid ${LINE}`,
    boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
    "& .MuiMenuItem-root": { fontFamily: FONT, fontSize: 13.5, borderRadius: "8px", mx: 0.5, minHeight: 34 },
  },
};
const th = {
  padding: "10px 16px",
  textAlign: "left",
  background: "#F7F9FC",
  borderBottom: `1px solid ${LINE}`,
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: MUTED,
  whiteSpace: "nowrap",
};
const td = {
  padding: "11px 16px",
  borderBottom: "1px solid #F1F4F8",
  verticalAlign: "middle",
  fontFamily: FONT,
  fontSize: 13.5,
  color: INK,
};

const OPTIONAL_COLUMNS = [
  { key: "assignedBy", label: "Assigned by" },
  { key: "assignedTo", label: "Assigned to" },
  { key: "date", label: "Reminder date" },
  { key: "status", label: "Status" },
];

const Reminders = () => {
  const [reminders, setReminders] = useState([]);
  // Which set is shown: UPCOMING | TODAY | TEAM | PAST (the stat cards switch it)
  const [activeTab, setActiveTab] = useState("UPCOMING");
  const storedUser = JSON.parse(localStorage.getItem("user"));
  // const [role, setRole] = useState();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 750px)");
  const orgId = JSON.parse(localStorage.getItem("org"));
  const orgCtx = useOrganization();
  const { role } = orgCtx;

  // table controls (display only)
  const [search, setSearch] = useState("");
  const [byFilter, setByFilter] = useState("");
  const [toFilter, setToFilter] = useState("");
  const [byAnchor, setByAnchor] = useState(null);
  const [toAnchor, setToAnchor] = useState(null);
  const [colAnchor, setColAnchor] = useState(null);
  const [hiddenCols, setHiddenCols] = useState([]);
  const [page, setPage] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("error");
  const showModal = (msg, type = "error") => {
    setModalMsg(msg);
    setModalType(type);
    setModalOpen(true);
  };

  // Delete confirmation (replaces window.confirm)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // useEffect(() => {
  //   if (!storedUser?.accessToken) return;
  //   const decoded = jwtDecode(storedUser.accessToken);
  //   const role = decoded.userRole[0];
  //   setRole(role);
  // }, []);

  useEffect(() => {
    const fetchReminders = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/reminder?orgId=${orgId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
        );
        const reminders = res.data || [];

        reminders.forEach((reminder) => {
          scheduleFollowUpReminder(reminder);
        });
      } catch (error) {
        showModal(error?.response?.data?.Exception || "Failed to load reminders");
        console.error(error);
      }
    };
    fetchReminders();
  }, [orgId]);

  useEffect(() => {
    const fetchReminders = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/reminder/all?orgId=${orgId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
        );
        setReminders(res.data || []);
      } catch (error) {
        showModal(error?.response?.data?.Exception || "Failed to load reminders");
        console.error(error);
      }
    };
    fetchReminders();
  }, [orgId]);

  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const upcomingReminders = useMemo(() => {
    return reminders
      .filter((r) => {
        const rd = normalizeDate(r.dateTime);
        return rd >= today;
      })
      .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
  }, [reminders, today]);

  const pastReminders = useMemo(() => {
    return reminders
      .filter((r) => {
        const rd = normalizeDate(r.dateTime);
        return rd < today;
      })
      .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
  }, [reminders, today]);

  const todayReminders = useMemo(
    () =>
      upcomingReminders.filter(
        (r) => normalizeDate(r.dateTime).getTime() === today.getTime(),
      ),
    [upcomingReminders, today],
  );

  const teamReminders = useMemo(
    () =>
      upcomingReminders.filter(
        (r) => r.reminderFor !== null && r.reminderFor !== "SELF" && r.reminderFor !== "USER",
      ),
    [upcomingReminders],
  );

  const isCreatedByMe = (reminder) => {
    if (!storedUser) return false;

    // SELF reminder
    if (reminder.reminderFor === "SELF") return true;

    // Admin-created reminder
    if (
      reminder.adminCreated &&
      reminder.adminCreated.adminMobile === storedUser.username
    ) {
      return true;
    }

    // Org user–created reminder
    if (
      reminder.createdBy &&
      reminder.createdBy.mobile === storedUser.username
    ) {
      return true;
    }

    return false;
  };

  // Display labels (same data the old columns showed)
  const assignedByLabel = (r) => {
    if (r.reminderFor !== null && r.reminderFor === "SELF") return "You";
    if (r.adminCreated !== null && r.adminCreated !== undefined) {
      return r.adminCreated?.adminMobile === storedUser?.username ? "You" : "Admin";
    }
    if (r?.createdBy?.mobile === storedUser?.username) return "You";
    return r?.createdBy?.name || "—";
  };

  const assignedToLabel = (r) => {
    if (r.reminderFor === null) return "—";
    if (r.reminderFor === "SELF") return "You";
    if (r.reminderFor === "USER") {
      if (
        r?.orgUser?.mobile === storedUser?.username ||
        r?.forAdmin?.adminMobile === storedUser?.username
      )
        return "You";
      return r?.forAdmin?.adminEmail || r?.orgUser?.name || "User";
    }
    return r?.team?.name || "Team";
  };

  const isForMe = (r) => assignedToLabel(r) === "You";

  const statusOf = (r) => {
    const d = normalizeDate(r.dateTime).getTime();
    if (d < today.getTime()) return "Past";
    if (d === today.getTime()) return "Today";
    return "Pending";
  };

  const baseRows =
    activeTab === "PAST"
      ? pastReminders
      : activeTab === "TODAY"
        ? todayReminders
        : activeTab === "TEAM"
          ? teamReminders
          : upcomingReminders;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return baseRows.filter((r) => {
      if (byFilter && assignedByLabel(r) !== byFilter) return false;
      if (toFilter && assignedToLabel(r) !== toFilter) return false;
      if (!q) return true;
      return [r.text, assignedByLabel(r), assignedToLabel(r), formatDateTime(r.dateTime)]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [baseRows, search, byFilter, toFilter]);

  useEffect(() => setPage(0), [activeTab, search, byFilter, toFilter]);

  const byOptions = [...new Set(reminders.map(assignedByLabel))].sort();
  const toOptions = [...new Set(reminders.map(assignedToLabel))].sort();

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const show = (key) => !hiddenCols.includes(key);

  const goEdit = (reminder) =>
    navigate(
      role === "ADMIN"
        ? `/dashboard/reminder/edit/${reminder.reminderId}`
        : `/dashboard/reminder/edit/${reminder.reminderId}`
    );

  // Delete button opens the confirm popup; "Delete" in the popup runs this.
  const handleRemoveReminder = (reminderId) => setConfirmDeleteId(reminderId);

  const deleteReminder = async (reminderId) => {
    setConfirmDeleteId(null);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/reminder?reminderId=${reminderId}`,
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        }
      );

      setReminders((prev) => prev.filter((r) => r.reminderId !== reminderId));
      localStorage.removeItem(`reminder_${reminderId}`);
    } catch (err) {
      showModal("Failed to delete reminder");
    }
  };

  function formatDateTime(dateString) {
    if (!dateString) return "";

    const date = new Date(dateString);

    const formattedDate = date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return `${formattedDate} ${formattedTime}`;
  }

  const formatDay = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "";

  const formatTime = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "";

  /* ---------- small render helpers ---------- */
  const pill = (label) => {
    const p = PILL[label];
    return (
      <span
        style={{
          display: "inline-block",
          padding: "3px 10px",
          borderRadius: 999,
          background: p.bg,
          border: `1px solid ${p.edge}`,
          color: p.color,
          fontFamily: FONT,
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    );
  };

  const nextUp = upcomingReminders[0];
  const stats = [
    {
      key: "UPCOMING",
      label: "Pending",
      value: upcomingReminders.length,
      sub: nextUp ? `next at ${formatTime(nextUp.dateTime)}` : "nothing coming up",
    },
    {
      key: "TODAY",
      label: "Today",
      value: todayReminders.length,
      sub: `${todayReminders.filter(isForMe).length} assigned to you`,
    },
    {
      key: "TEAM",
      label: "For the team",
      value: teamReminders.length,
      sub: "team reminders",
    },
    {
      key: "PAST",
      label: "Past",
      value: pastReminders.length,
      sub: "before today",
    },
  ];

  const statCard = (s) => {
    const t = STAT_THEMES[s.key];
    const on = activeTab === s.key;
    return (
      <button
        key={s.key}
        type="button"
        onClick={() => setActiveTab(s.key)}
        style={{
          textAlign: "left",
          padding: "14px 16px",
          background: t.bg,
          border: `1px solid ${t.edge}`,
          borderRadius: 14,
          cursor: "pointer",
          fontFamily: FONT,
          boxShadow: on ? `0 0 0 2px ${t.num}33, 0 6px 18px -12px ${t.num}` : "none",
          outline: "none",
        }}
      >
        <div style={{ ...metaLabel, color: t.label }}>{s.label.toUpperCase()}</div>
        <div
          style={{
            fontFamily: FONT,
            fontSize: 30,
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            color: t.num,
            margin: "4px 0 2px",
          }}
        >
          {s.value}
        </div>
        <div style={{ fontSize: 12.5, color: INK_2 }}>{s.sub}</div>
      </button>
    );
  };

  const filterMenu = (anchor, setAnchor, options, value, setValue) => (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={() => setAnchor(null)}
      PaperProps={menuPaper}
    >
      <MenuItem
        selected={!value}
        onClick={() => {
          setValue("");
          setAnchor(null);
        }}
      >
        Anyone
      </MenuItem>
      {options.map((o) => (
        <MenuItem
          key={o}
          selected={value === o}
          onClick={() => {
            setValue(o);
            setAnchor(null);
          }}
        >
          {o}
        </MenuItem>
      ))}
    </Menu>
  );

  const actionButtons = (r, iconOnly) =>
    isCreatedByMe(r) ? (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => goEdit(r)}
          sx={iconOnly ? { ...smallBtn, width: 40, height: 40, px: 0 } : smallBtn}
        >
          {iconOnly ? <EditIcon sx={{ fontSize: 18 }} /> : "Edit"}
        </Button>
        <Button
          size="small"
          color="error"
          variant="outlined"
          onClick={() => handleRemoveReminder(r.reminderId)}
          sx={iconOnly ? { ...dangerBtn, width: 40, height: 40, px: 0 } : dangerBtn}
        >
          {iconOnly ? <DeleteIcon sx={{ fontSize: 18 }} /> : "Delete"}
        </Button>
      </Box>
    ) : null;

  const emptyText = {
    UPCOMING: "No upcoming reminders.",
    TODAY: "No reminders today.",
    TEAM: "No team reminders.",
    PAST: "No past reminders.",
  }[activeTab];

  const orgName =
    orgCtx?.orgName ||
    orgCtx?.organization?.orgName ||
    orgCtx?.organization?.name ||
    orgCtx?.org?.orgName ||
    "";

  return (
    <div style={{ fontFamily: FONT, color: INK, textAlign: "left", paddingBottom: 24 }}>
      {/* ---------------- HEADER ---------------- */}
      <Box
        sx={{
          display: { xs: "block", sm: "flex" },
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 2,
          mt: 1,
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontFamily: FONT,
              fontSize: isMobile ? 24 : 28,
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: 1.15,
              color: INK,
            }}
          >
            Reminders
          </h1>
          <div style={{ marginTop: 6, fontSize: 13.5, color: INK_2 }}>
            {[orgName, `${rows.length} of ${reminders.length} reminders`]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
        <Button
          className="btn-contained"
          sx={{
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1.5, sm: 0 },
            height: 38,
            px: 2,
            margin: "0 !important",
            borderRadius: "10px !important",
            background: `${PRIMARY} !important`,
            color: "#FFFFFF !important",
            border: "0 !important",
            fontFamily: FONT,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { background: `${PRIMARY_DARK} !important` },
          }}
          onClick={() =>
            navigate(
              role === "ADMIN"
                ? "/dashboard/reminder/add-reminder"
                : "/dashboard/reminder/add-reminder"
            )
          }
        >
          Add reminder
        </Button>
      </Box>

      {/* ---------------- STAT CARDS ---------------- */}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          mb: 1.5,
          gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "repeat(4, minmax(0,1fr))" },
        }}
      >
        {stats.map(statCard)}
      </Box>

      {/* ---------------- TABLE CARD ---------------- */}
      <Box sx={{ background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: "14px", overflow: "hidden" }}>
        {/* toolbar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            p: "12px 16px",
            borderBottom: `1px solid ${LINE}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              height: 32,
              px: 1.25,
              width: { xs: "100%", sm: 200 },
              boxSizing: "border-box",
              background: FIELD_BG,
              border: `1px solid ${FIELD_EDGE}`,
              borderRadius: "999px",
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: "#8A97A8" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reminders"
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                outline: "none",
                background: "transparent",
                fontFamily: FONT,
                fontSize: 12.5,
                color: INK,
                margin: 0,
                padding: 0,
                height: "100%",
              }}
            />
          </Box>

          <Button onClick={(e) => setByAnchor(e.currentTarget)} sx={pillBtn(!!byFilter)}>
            {byFilter ? `By: ${byFilter}` : "Assigned by"}
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />
          </Button>
          <Button onClick={(e) => setToAnchor(e.currentTarget)} sx={pillBtn(!!toFilter)}>
            {toFilter ? `To: ${toFilter}` : "Assigned to"}
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />
          </Button>

          {!isMobile && (
            <Button
              onClick={(e) => setColAnchor(e.currentTarget)}
              sx={{ ...pillBtn(hiddenCols.length > 0), ml: "auto" }}
            >
              Columns
            </Button>
          )}

          {filterMenu(byAnchor, setByAnchor, byOptions, byFilter, setByFilter)}
          {filterMenu(toAnchor, setToAnchor, toOptions, toFilter, setToFilter)}
          <Menu
            anchorEl={colAnchor}
            open={Boolean(colAnchor)}
            onClose={() => setColAnchor(null)}
            PaperProps={menuPaper}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {OPTIONAL_COLUMNS.map((c) => (
              <MenuItem
                key={c.key}
                onClick={() =>
                  setHiddenCols((prev) =>
                    prev.includes(c.key) ? prev.filter((k) => k !== c.key) : [...prev, c.key],
                  )
                }
              >
                <Checkbox
                  size="small"
                  checked={show(c.key)}
                  sx={{ p: 0.5, mr: 1, color: "#C3CDD9", "&.Mui-checked": { color: PRIMARY } }}
                />
                <ListItemText primary={c.label} primaryTypographyProps={{ fontFamily: FONT, fontSize: 13.5 }} />
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* empty state */}
        {rows.length === 0 && (
          <Box sx={{ textAlign: "center", p: "36px 20px" }}>
            <Box
              sx={{
                position: "relative",
                width: 120,
                height: 120,
                margin: "0 auto 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div style={{ position: "absolute", inset: 0, background: "#EAF2FB", borderRadius: "50%" }} />
              <div
                style={{
                  position: "relative",
                  transform: "rotate(-12.15deg)",
                  borderRadius: "20px",
                  background: "#FFFFFF",
                  border: "1px solid #C9DEF3",
                  width: "68px",
                  height: "68px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 20px -10px rgba(11,92,138,0.4)",
                }}
              >
                <NotificationsIcon sx={{ width: "30px", height: "30px", color: PRIMARY, transform: "rotate(12.15deg)" }} />
              </div>
            </Box>
            <Typography
              fontSize={"14px"}
              textTransform={"uppercase"}
              fontWeight={700}
              color={MUTED}
              sx={{ fontFamily: FONT, letterSpacing: "0.08em" }}
            >
              {emptyText}
            </Typography>
          </Box>
        )}

        {/* mobile cards */}
        {rows.length > 0 && isMobile && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {pageRows.map((r) => (
              <div
                key={r.reminderId}
                className="card"
                style={{ margin: 0, padding: "14px 16px", background: "#FFFFFF", borderBottom: "1px solid #F1F4F8", boxShadow: "none" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: INK, textWrap: "pretty" }}>{r.text}</div>
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                      {formatDay(r.dateTime)} · {formatTime(r.dateTime)}
                    </div>
                  </div>
                  {pill(statusOf(r))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 10, marginTop: 10 }}>
                  <div style={{ fontSize: 12.5, color: INK_2 }}>
                    <span style={{ color: MUTED }}>By</span> {assignedByLabel(r)}
                    <span style={{ color: MUTED }}> · To</span> {assignedToLabel(r)}
                  </div>
                  {actionButtons(r, true)}
                </div>
              </div>
            ))}
          </Box>
        )}

        {/* desktop table */}
        {rows.length > 0 && !isMobile && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: "40%" }}>Description</th>
                  {show("assignedBy") && <th style={th}>Assigned by</th>}
                  {show("assignedTo") && <th style={th}>Assigned to</th>}
                  {show("date") && <th style={th}>Reminder date</th>}
                  {show("status") && <th style={th}>Status</th>}
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr
                    key={r.reminderId}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FBFD")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                  >
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.text}</div>
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                        {formatDay(r.dateTime)} · {formatTime(r.dateTime)}
                      </div>
                    </td>
                    {show("assignedBy") && <td style={td}>{assignedByLabel(r)}</td>}
                    {show("assignedTo") && <td style={td}>{assignedToLabel(r)}</td>}
                    {show("date") && (
                      <td style={{ ...td, fontFamily: MONO, fontSize: 12.5, color: INK_2, whiteSpace: "nowrap" }}>
                        {formatDay(r.dateTime)}
                      </td>
                    )}
                    {show("status") && <td style={td}>{pill(statusOf(r))}</td>}
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>{actionButtons(r, false)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* footer */}
        {rows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              p: "10px 16px",
            }}
          >
            <span style={{ fontSize: 12.5, color: MUTED }}>
              Showing {pageRows.length} of {rows.length} reminders
            </span>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                sx={{ ...smallBtn, "&.Mui-disabled": { opacity: 0.5, color: MUTED } }}
              >
                Previous
              </Button>
              <Button
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1}
                sx={{ ...smallBtn, "&.Mui-disabled": { opacity: 0.5, color: MUTED } }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <ErrorModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        msg={modalMsg}
        type={modalType}
      />

      {/* Delete confirmation */}
      <Dialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "18px", p: "24px 22px 20px", textAlign: "center", fontFamily: FONT },
        }}
        slotProps={{ backdrop: { sx: { background: "rgba(15,27,42,0.46)" } } }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            margin: "0 auto 12px",
            borderRadius: "50%",
            background: "#FBEDEB",
            border: "1px solid #F2CFCA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DeleteIcon sx={{ fontSize: 26, color: "#C42B24" }} />
        </div>
        <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK }}>
          Delete reminder?
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 14, color: "#4A5A6E", mt: 0.75, mb: 2.5 }}>
          Are you sure you want to delete this reminder?
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Button
            fullWidth
            onClick={() => setConfirmDeleteId(null)}
            sx={{ ...smallBtn, height: 42, fontSize: 14 }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            onClick={() => deleteReminder(confirmDeleteId)}
            sx={{
              height: 42,
              borderRadius: "8px",
              background: "#C42B24",
              color: "#FFFFFF",
              fontFamily: FONT,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: "#A6342A" },
            }}
          >
            Delete
          </Button>
        </Box>
      </Dialog>
    </div>
  );
};

export default Reminders;